/**
 * SuperadminSupportTickets.jsx – Support-Ticket-Verwaltung für Superadmin
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Ticket, Search, ChevronDown, Send, RefreshCw, Tag, Clock,
  CheckCircle2, AlertCircle, MessageSquare, X, Loader2, Filter,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_LABELS = { open: 'Offen', in_progress: 'In Bearbeitung', answered: 'Beantwortet', closed: 'Geschlossen' };
const STATUS_COLORS = { open: 'bg-red-100 text-red-800', in_progress: 'bg-yellow-100 text-yellow-800', answered: 'bg-blue-100 text-blue-800', closed: 'bg-gray-100 text-gray-600' };
const URGENCY_COLORS = { kritisch: 'bg-red-500', stoerend: 'bg-yellow-500', kleinigkeit: 'bg-green-500' };
const URGENCY_LABELS = { kritisch: 'Kritisch', stoerend: 'Störend', kleinigkeit: 'Kleinigkeit' };

function StatusBadge({ status }) {
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}>{STATUS_LABELS[status] || status}</span>;
}

function UrgencyDot({ urgency }) {
  return <span className={`w-2.5 h-2.5 rounded-full inline-block flex-shrink-0 ${URGENCY_COLORS[urgency] || 'bg-gray-400'}`} />;
}

export default function SuperadminSupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUrgency, setFilterUrgency] = useState('');
  const [filterType, setFilterType] = useState('');
  const [page, setPage] = useState(1);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '30' });
      if (filterStatus) params.set('status', filterStatus);
      if (filterUrgency) params.set('urgency', filterUrgency);
      if (filterType) params.set('ticket_type', filterType);
      const res = await axios.get(`${API}/api/superadmin/support/tickets?${params}`, { headers: headers() });
      setTickets(res.data.tickets || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Fehler beim Laden der Tickets'); }
    finally { setLoading(false); }
  }, [page, filterStatus, filterUrgency, filterType]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const openTicket = async (t) => {
    const res = await axios.get(`${API}/api/superadmin/support/tickets/${t.id}`, { headers: headers() });
    setSelected(res.data);
    setNewStatus(res.data.status);
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selected) return;
    setReplying(true);
    try {
      await axios.post(`${API}/api/superadmin/support/tickets/${selected.id}/reply`,
        { text: replyText, new_status: newStatus || 'answered' }, { headers: headers() });
      toast.success('Antwort gesendet');
      setReplyText('');
      await openTicket(selected);
      fetchTickets();
    } catch { toast.error('Fehler beim Senden'); }
    finally { setReplying(false); }
  };

  const handleStatusChange = async (ticketId, status) => {
    await axios.patch(`${API}/api/superadmin/support/tickets/${ticketId}/status`, { status }, { headers: headers() });
    toast.success(`Status: ${STATUS_LABELS[status]}`);
    fetchTickets();
    if (selected?.id === ticketId) setSelected(s => ({ ...s, status }));
  };

  return (
    <div className="flex h-full gap-0 overflow-hidden">
      {/* ── Ticket-Liste (links) ── */}
      <div className={`flex flex-col border-r border-border bg-background ${selected ? 'hidden lg:flex lg:w-80' : 'flex-1'}`}>
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h1 className="font-bold text-base flex items-center gap-2">
              <Ticket className="h-5 w-5 text-primary" />
              Support-Tickets
              {total > 0 && <span className="ml-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{total}</span>}
            </h1>
            <Button size="sm" variant="ghost" onClick={fetchTickets} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          {/* Filter */}
          <div className="space-y-1.5">
            <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
              className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2">
              <option value="">Alle Status</option>
              {Object.entries(STATUS_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <div className="flex gap-1.5">
              <select value={filterUrgency} onChange={e => setFilterUrgency(e.target.value)}
                className="flex-1 h-8 text-xs rounded-lg border border-border bg-background px-2">
                <option value="">Alle Prioritäten</option>
                <option value="kritisch">Kritisch</option>
                <option value="stoerend">Störend</option>
                <option value="kleinigkeit">Kleinigkeit</option>
              </select>
              <select value={filterType} onChange={e => setFilterType(e.target.value)}
                className="flex-1 h-8 text-xs rounded-lg border border-border bg-background px-2">
                <option value="">Alle Typen</option>
                <option value="fehlermeldung">Fehlermeldung</option>
                <option value="support_frage">Supportanfrage</option>
              </select>
            </div>
          </div>
        </div>

        {/* Liste */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : tickets.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">Keine Tickets</p>
          ) : (
            tickets.map(t => (
              <button key={t.id} onClick={() => openTicket(t)}
                className={`w-full text-left px-4 py-3 border-b border-border/50 hover:bg-muted/30 transition-colors ${selected?.id === t.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''}`}
                data-testid={`ticket-row-${t.ticket_number}`}>
                <div className="flex items-start gap-2">
                  <UrgencyDot urgency={t.urgency} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-xs font-bold text-primary">#{t.ticket_number}</span>
                      <StatusBadge status={t.status} />
                      {t.replies?.length > 0 && (
                        <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                          <MessageSquare className="h-3 w-3" />{t.replies.length}
                        </span>
                      )}
                    </div>
                    <p className="text-xs font-medium text-foreground truncate">{t.description?.slice(0,60)}{t.description?.length > 60 ? '…' : ''}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{t.company_name || '–'} · {new Date(t.created_at).toLocaleDateString('de-DE')}</p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Pagination */}
        {total > 30 && (
          <div className="p-3 border-t flex gap-2 justify-center">
            <Button size="sm" variant="outline" disabled={page===1} onClick={() => setPage(p=>p-1)}>Zurück</Button>
            <Button size="sm" variant="outline" disabled={page*30>=total} onClick={() => setPage(p=>p+1)}>Weiter</Button>
          </div>
        )}
      </div>

      {/* ── Ticket-Detail (rechts) ── */}
      {selected ? (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Detail-Header */}
          <div className="p-4 border-b border-border flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-bold text-primary">#{selected.ticket_number}</span>
                <StatusBadge status={selected.status} />
                <span className="text-xs text-muted-foreground">{URGENCY_LABELS[selected.urgency] || selected.urgency}</span>
                <span className="text-xs text-muted-foreground">{selected.type === 'support_frage' ? '· Supportanfrage' : '· Fehlermeldung'}</span>
              </div>
              <p className="text-sm text-muted-foreground">{selected.company_name} · {selected.reported_by_email}</p>
              <p className="text-xs text-muted-foreground">{new Date(selected.created_at).toLocaleString('de-DE')} · Seite: {selected.page}</p>
            </div>
            <div className="flex items-center gap-2">
              <select value={selected.status} onChange={e => handleStatusChange(selected.id, e.target.value)}
                className="h-8 text-xs rounded-lg border border-border bg-background px-2">
                {Object.entries(STATUS_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <Button size="sm" variant="ghost" onClick={() => setSelected(null)}><X className="h-4 w-4"/></Button>
            </div>
          </div>

          {/* Thread */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Original */}
            <div className="bg-muted/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-7 w-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                  {(selected.reported_by_name || selected.reported_by_email)?.[0]?.toUpperCase()}
                </div>
                <div>
                  <p className="text-xs font-semibold">{selected.reported_by_name || selected.reported_by_email}</p>
                  <p className="text-[10px] text-muted-foreground">{new Date(selected.created_at).toLocaleString('de-DE')}</p>
                </div>
              </div>
              <p className="text-sm text-foreground whitespace-pre-wrap">{selected.description}</p>
            </div>

            {/* Replies */}
            {(selected.replies || []).map(r => (
              <div key={r.id} className={`rounded-xl p-4 ${r.author === 'AllFlowX Support' ? 'bg-primary/5 border border-primary/20 ml-8' : 'bg-muted/30 mr-8'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${r.author === 'AllFlowX Support' ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}>
                    {r.author[0]}
                  </div>
                  <div>
                    <p className="text-xs font-semibold">{r.author}</p>
                    <p className="text-[10px] text-muted-foreground">{new Date(r.timestamp).toLocaleString('de-DE')}</p>
                  </div>
                  {r.sent_email && <span className="ml-auto text-[10px] text-green-600 flex items-center gap-0.5"><CheckCircle2 className="h-3 w-3"/>E-Mail gesendet</span>}
                </div>
                <p className="text-sm text-foreground whitespace-pre-wrap">{r.text}</p>
              </div>
            ))}
          </div>

          {/* Reply-Box */}
          {selected.status !== 'closed' && (
            <div className="p-4 border-t border-border space-y-2">
              <textarea
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder="Antwort schreiben (wird per E-Mail an Kunden gesendet)…"
                rows={3}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="reply-textarea"
              />
              <div className="flex items-center gap-2">
                <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
                  className="h-9 text-xs rounded-lg border border-border bg-background px-2">
                  {Object.entries(STATUS_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <Button onClick={handleReply} disabled={!replyText.trim() || replying}
                  className="gap-1.5" data-testid="reply-send-btn">
                  {replying ? <Loader2 className="h-4 w-4 animate-spin"/> : <Send className="h-4 w-4"/>}
                  Antworten & E-Mail senden
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="hidden lg:flex flex-1 items-center justify-center text-muted-foreground flex-col gap-3">
          <Ticket className="h-12 w-12 opacity-20"/>
          <p className="text-sm">Ticket auswählen</p>
        </div>
      )}
    </div>
  );
}
